import {
  PaymentMethod,
  PaymentSortOption,
  PaymentStatus,
  PaymentType,
  enumPaymentSortOption,
} from '@rematter/frontend-lib-api-client';
import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';

import { billAccountsQueryAtom } from '../_graphql/queryBillAccounts';
import { paymentsQueryAtom } from '../_graphql/queryFinancePayments';
import { financialAccountsFilterQueryAtom } from '../_graphql/queryFinancialAccountsFilter';

import { PaymentRecord } from '~/modules/finance/types';
import { atomFromQueryString } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $searchTerm = atomFromQueryString.string('q');
export const $columnSelectedStatuses =
  atomFromQueryString.stringSet<PaymentStatus>('colStatuses');
export const $columnSelectedTypes =
  atomFromQueryString.stringSet<PaymentType>('colTypes');
export const $columnSelectedMethods =
  atomFromQueryString.stringSet<PaymentMethod>('colMethods');
export const $columnSelectedAccountIds = atomFromQueryString.stringSet('acc');
export const $columnSelectedFinancialAccountIds =
  atomFromQueryString.stringSet('finAcc');
export const $columnDateRange = atomFromQueryString.dateRange();
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);

// 🔘🔘🔘 PLAIN ATOMS
export const $selectedKeys = atom<DataGridSelection>(new Set<React.Key>());
export const $isApplyAdvanceDrawerOpen = atom<boolean>(false);
export const $selectedPayment = atom<PaymentRecord | null>(null);
export const $currentSorting = atom<PaymentSortOption[]>([
  enumPaymentSortOption.DateDesc,
]);

// 🔘🔘🔘 FILTER ATOMS
export const $accountFilterSearchTerm = atom<string | undefined>(undefined);
export const $financialAccountFilterSearchTerm = atom<string | undefined>(
  undefined,
);

// 🟢🟢🟢 FILTER REMOTE DATA
export const $remoteFilterAccounts = billAccountsQueryAtom((get) => {
  const searchTerm = get($accountFilterSearchTerm);
  return {
    variables: { searchTerm, limit: 20 },
  };
});

export const $remoteFilterFinancialAccounts = financialAccountsFilterQueryAtom(
  (get) => {
    const searchTerm = get($financialAccountFilterSearchTerm);
    return {
      variables: { searchTerm, limit: 20 },
    };
  },
);

// 🟢🟢🟢 REMOTE DATA
export const $remotePayments = paymentsQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const columnSelectedStatuses = get($columnSelectedStatuses);
  const columnSelectedTypes = get($columnSelectedTypes);
  const columnSelectedMethods = get($columnSelectedMethods);
  const columnSelectedAccountIds = get($columnSelectedAccountIds);
  const columnSelectedFinancialAccountIds = get(
    $columnSelectedFinancialAccountIds,
  );
  const columnDateRange = get($columnDateRange);
  const limit = get($limit);
  const offset = get($offset);
  const currentSorting = get($currentSorting);

  const params: {
    searchTerm?: string;
    statuses?: PaymentStatus[];
    types?: PaymentType[];
    paymentMethods?: PaymentMethod[];
    accountIDs?: string[];
    financialAccountIDs?: string[];
    dateRangeStart?: string;
    dateRangeEnd?: string;
    limit?: number;
    offset?: number;
    sorting: PaymentSortOption[];
  } = {
    limit,
    offset,
    sorting: currentSorting,
  };

  if (searchTerm) params.searchTerm = searchTerm;
  if (columnSelectedStatuses.size > 0)
    params.statuses = Array.from(columnSelectedStatuses);
  if (columnSelectedTypes.size > 0)
    params.types = Array.from(columnSelectedTypes);
  if (columnSelectedMethods.size > 0)
    params.paymentMethods = Array.from(columnSelectedMethods);
  if (columnSelectedAccountIds.size > 0)
    params.accountIDs = Array.from(columnSelectedAccountIds);
  if (columnSelectedFinancialAccountIds.size > 0)
    params.financialAccountIDs = Array.from(columnSelectedFinancialAccountIds);
  if (columnDateRange?.start)
    params.dateRangeStart = columnDateRange.start.toString();
  if (columnDateRange?.end)
    params.dateRangeEnd = columnDateRange.end.toString();

  return {
    variables: params,
  };
});
